import { toast, useToggleState } from '@medusajs/ui';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import type { Payment as PaymentPayment } from '@marxbiotech/shared-types/bff';
import {
  settlementRequestsQueryOptions,
  SettlementRequest,
} from '@lib/query/options/payment';
import { isLoanRequestPending } from '@lib/util/loan-request';
import { OrderPaymentNotificationButton } from './button';
import { OrderPaymentNotificationModal } from './modal';

interface OrderPaymentNotificationContainerProps {
  orderId: string;
  orderNumber: string;
  marxPayment?: PaymentPayment;
  isPerOrderSettlement: boolean;
}

// 僅在「先付後送」客戶且有 PENDING LoanRequest 時顯示
export const OrderPaymentNotificationContainer = ({
  orderId,
  orderNumber,
  marxPayment,
  isPerOrderSettlement,
}: OrderPaymentNotificationContainerProps) => {
  const {
    state: isModalOpen,
    open: openModal,
    close: closeModal,
  } = useToggleState(false);

  const loanRequest = marxPayment?.loanRequest;
  const shouldShow =
    isPerOrderSettlement && loanRequest && isLoanRequestPending(loanRequest);

  const { data: settlementRequests = [], isPending: isLoadingRequests } =
    useQuery({
      ...settlementRequestsQueryOptions(loanRequest?.id ?? ''),
      enabled: !!shouldShow && !!loanRequest?.id,
    });

  const pendingSettlementRequest = useMemo(
    () =>
      settlementRequests.find(
        (sr: SettlementRequest) =>
          sr.approvedAt === null && sr.rejectedAt === null
      ),
    [settlementRequests]
  );

  if (!shouldShow || !loanRequest) {
    return null;
  }

  const handleButtonClick = () => {
    if (pendingSettlementRequest) {
      toast.info('此訂單已有待審核的繳款通知');
      return;
    }
    openModal();
  };

  return (
    <>
      <OrderPaymentNotificationButton
        hasPendingRequest={!!pendingSettlementRequest}
        isLoading={isLoadingRequests}
        onClick={handleButtonClick}
      />
      <OrderPaymentNotificationModal
        isOpen={isModalOpen}
        onClose={closeModal}
        loanRequest={loanRequest}
        orderId={orderId}
        orderNumber={orderNumber}
      />
    </>
  );
};

export default OrderPaymentNotificationContainer;
